import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/authContext';
import "./index.css";
import { useAuth } from './contexts/authContext';
import axios from 'axios';
import { ReturnProcessProvider } from './contexts/ReturnProcessContext';


// Components
const Spinner = () => <div className='spinner'></div>;

// Page Imports (organized by category)
const publicPages = {
  Welcome: React.lazy(() => import('./pages/Welcome')),
  Auth: React.lazy(() => import('./pages/Auth')),
  ResetPasswordPage: React.lazy(() => import('./pages/ResetPasswordPage')),
  ChooseVerification: React.lazy(() => import('./pages/ChooseVerification')),
  EmailVerification: React.lazy(() => import('./pages/Email')),
  Verify: React.lazy(() => import('./pages/Verify')),
  VerifyEmail: React.lazy(() => import('./pages/VerifyEmail')),
  PushNotification: React.lazy(() => import('./pages/PushNotification')),
  Address: React.lazy(() => import('./pages/Address')),
  PasswordReset: React.lazy(() => import('./components/PasswordReset')),
};

const protectedPages = {
  Home: React.lazy(() => import('./pages/Home')),
  Purchases: React.lazy(() => import('./pages/Purchases')),
  ProductDetail: React.lazy(() => import('./pages/ProductDetail')),
  ProductEdit: React.lazy(() => import('./pages/ProductEdit')),
  AddPurchase: React.lazy(() => import('./pages/AddPurchase')),
  AmazonOrders: React.lazy(() => import('./pages/AmazonOrder')),
  GoogleOrder: React.lazy(() => import('./pages/GoogleOrder')),
  Plan: React.lazy(() => import('./pages/Plan')),
  PaymentPage: React.lazy(() => import('./pages/Payment')),
  ReturnBag: React.lazy(() => import('./pages/ReturnBag')),

  ReturnProcess: React.lazy(()=>import('./components/ReturnProcessLayout')),
  LabelUpload: React.lazy(() => import('./pages/LabelUpload')),
  PackageType: React.lazy(() => import('./pages/Package')),
  LabelPackage: React.lazy(()=>import('./pages/LabelPackage')),
  ReturnPickupPage: React.lazy(() => import('./pages/ReturnBagPickup')),
  SchedulePickup: React.lazy(() => import('./pages/SchedulePickup')),
  DropoffLocation: React.lazy(() => import('./pages/DropoffLocation')),
  ConfirmationPage: React.lazy(() => import('./pages/Confirm')),

  Account: React.lazy(() => import('./pages/Account')),
  CheckoutPage: React.lazy(() => import('./pages/Checkout')),
  Pickup: React.lazy(() => import('./pages/Pickup')),
  HelpPage: React.lazy(() => import('./pages/Help')),
  GetAddress: React.lazy(() => import('./pages/GetAddress')),
  NotificationSettingsPage: React.lazy(() => import('./pages/Notification')),
  PaymentMethodPage: React.lazy(() => import('./pages/PaymentMethod')),
  InviteFriend: React.lazy(() => import('./pages/InviteFriend')),
  SendBackPlan: React.lazy(() => import('./pages/ManagePlan')),
  // Test: React.lazy(() => import('./pages/Test')),
};



const adminPages = {
  AdminPage: React.lazy(() => import('./pages/Admin')),
  AdminDashboard: React.lazy(() => import('./pages/AdminDashboard')),
  CustomerAdminPage: React.lazy(() => import('./pages/AdminCustomer')),
  OrderAdminPage: React.lazy(() => import('./pages/AdminOrder')),
  AdminPickupsPage: React.lazy(() => import('./pages/AdminPickups')),
};

const App = () => {

  return (
    <AuthProvider>
      <ReturnProcessProvider>
      <React.Suspense fallback={<Spinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/welcome" element={<publicPages.Welcome />} />
          <Route path="/auth" element={<publicPages.Auth />} />
          <Route path="/reset-password" element={<publicPages.ResetPasswordPage /> } />
          <Route path="/choose-verification" element={<publicPages.ChooseVerification /> } />
          <Route path="/email" element={<publicPages.EmailVerification /> } />
          <Route path="/verify/" element={ <publicPages.Verify />} />
          <Route path="/verify-email" element={<publicPages.VerifyEmail />} />
          {/* <Route path="/push-notification" element={!authState.isAuthenticated ? <publicPages.PushNotification /> : <Navigate to="/home" />} /> */}
          
          <Route path="/push-notification" element={<publicPages.PushNotification />} />
            <Route path="/address" element={<publicPages.Address />} />

          <Route path="/reset-password" element={ <publicPages.PasswordReset /> } />
          
          {/* Protected Routes */}
          <Route path="/home" element={ <protectedPages.Home /> } />
          <Route path="/purchases" element={<protectedPages.Purchases /> } />
          <Route path="/order/:productId" element={<protectedPages.ProductDetail /> } />
          <Route path="/order/edit/:productId" element={<protectedPages.ProductEdit /> } />
          <Route path="/add-purchase" element={<protectedPages.AddPurchase />} />
          <Route path="/amazon-orders" element={<protectedPages.AmazonOrders /> } />
          <Route path="/google-orders" element={<protectedPages.GoogleOrder /> } />
          <Route path="/plan" element={<protectedPages.Plan /> } />
          <Route path="/payment" element={<protectedPages.PaymentPage /> } />
          <Route path="/return-bag" element={<protectedPages.ReturnBag /> } />
          
          <Route path="/return" element={<protectedPages.ReturnProcess />} />
          <Route path="/upload-label" element={ <protectedPages.LabelUpload />} />
          <Route path="/package" element={ <protectedPages.PackageType /> } />
          <Route path="/label-package" element={<protectedPages.LabelPackage />} />
          <Route path="/dropoff-location" element={<protectedPages.ReturnPickupPage/>} />
          <Route path="/schedule-return" element={<protectedPages.SchedulePickup/>} />
          <Route path="/return-pickup" element={<protectedPages.DropoffLocation/>} />
          <Route path="/confirm" element={<protectedPages.ConfirmationPage/>} />

          <Route path="/account" element={<protectedPages.Account /> } />
          <Route path="/checkout" element={ <protectedPages.CheckoutPage /> } />
          <Route path="/pickups" element={<protectedPages.Pickup /> } />
          <Route path="/help" element={ <protectedPages.HelpPage /> } />
          <Route path="/get-address" element={ <protectedPages.GetAddress /> } />
          <Route path="/notifications" element={ <protectedPages.NotificationSettingsPage /> } />
          <Route path="/payment-methods" element={<protectedPages.PaymentMethodPage /> } />
          <Route path="/invite-friends" element={ <protectedPages.InviteFriend /> } />
          <Route path="/manage-sendback-plan" element={<protectedPages.SendBackPlan /> } />
          {/* <Route path="/test" element={authState.isAuthenticated ? <protectedPages.Test /> : <Navigate to="/login" />} /> */}
          
          {/* Admin Routes */}
          <Route path="/admin" element={<adminPages.AdminPage />} />
          <Route path="/admin/dashboard" element={<adminPages.AdminDashboard /> } />
          <Route path="/admin/customers" element={<adminPages.CustomerAdminPage /> } />
          <Route path="/admin/orders" element={<adminPages.OrderAdminPage /> } />
          <Route path="/admin/pickups" element={ <adminPages.AdminPickupsPage />} />

          {/* Root and Catch-all Routes */}
          <Route path="/" element={
              <AuthCheck>
                {(auth) => (
                  <Navigate to={
                    auth.isAuthenticated 
                      ? (auth.isNewUser ? '/push-notification' : '/home')
                      : '/welcome'
                  } />
                )}
              </AuthCheck>
            } />
          <Route path="*" element={
              <AuthCheck>
                {(auth) => (
                  <Navigate to={auth.isAuthenticated ? "/home" : "/welcome"} />
                )}
              </AuthCheck>
            } />
          </Routes>
        </React.Suspense>
      </ReturnProcessProvider>
    </AuthProvider>
  );
};

const AuthCheck = ({ children }) => {
  const auth = useAuth();
  return children(auth);
};

export default App;